import {
  type RovoContentBridgeAPI,
  type client,
} from '@atlassian/rovo-content-bridge-api';

import { WheelEventCommand } from '@canvas/databases-bridge-api/commands';

type ForwardWheelEventsParams = {
  bridge: RovoContentBridgeAPI;
  localClientId: ReturnType<typeof client>;
  externalClientId: ReturnType<typeof client>;
};

/**
 * Finds the scrollable ancestor element for a given element.
 * Returns the element itself if it's scrollable, or the nearest scrollable ancestor.
 */
function findScrollableContainer(element: Element | null): Element | null {
  if (!element) {
    return document.documentElement;
  }

  let current: Element | null = element;

  while (current) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;

    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight
    ) {
      return current;
    }

    if (
      (overflowX === 'auto' || overflowX === 'scroll') &&
      current.scrollWidth > current.clientWidth
    ) {
      return current;
    }

    if (current === document.documentElement || current === document.body) {
      return document.documentElement;
    }

    current = current.parentElement;
  }

  return document.documentElement;
}

/**
 * Checks if a scrollable container is at its scroll limits.
 * Returns true if scrolling in the given direction would exceed the bounds.
 */
function isAtScrollLimit(container: Element): {
  atTop: boolean;
  atBottom: boolean;
  atLeft: boolean;
  atRight: boolean;
} {
  const scrollTop = container.scrollTop;
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;

  const atTop = scrollTop <= 0;
  const atBottom = scrollTop + clientHeight >= scrollHeight;

  const scrollLeft = container.scrollLeft;
  const scrollWidth = container.scrollWidth;
  const clientWidth = container.clientWidth;

  const atLeft = scrollLeft <= 0;
  const atRight = scrollLeft + clientWidth >= scrollWidth;

  return { atTop, atBottom, atLeft, atRight };
}

/**
 * Determines if a wheel event should be forwarded to the parent whiteboard.
 * Wheel events are forwarded when:
 * - Ctrl or Meta key is pressed (zoom events should always be forwarded to the whiteboard)
 * - Scrolling down and at the bottom of a scrollable container
 * - Scrolling up and at the top of a scrollable container
 * - Scrolling right and at the right edge of a scrollable container
 * - Scrolling left and at the left edge of a scrollable container
 * - The container has no scroll capability (can't scroll at all)
 */
function shouldForwardWheelEvent(event: WheelEvent): boolean {
  if (event.ctrlKey || event.metaKey) {
    return true;
  }

  const target = event.target as Element;
  const container = findScrollableContainer(target);
  // If no scrollable container found, forward the event
  if (!container) {
    return true;
  }

  const { atTop, atBottom, atLeft, atRight } = isAtScrollLimit(container);

  // Vertical scrolling
  if (event.deltaY > 0) {
    // Scrolling down: forward only if at bottom
    return atBottom;
  }
  if (event.deltaY < 0) {
    // Scrolling up: forward only if at top
    return atTop;
  }

  // Horizontal scrolling
  if (event.deltaX > 0) {
    // Scrolling right: forward only if at right edge
    return atRight;
  }
  if (event.deltaX < 0) {
    // Scrolling left: forward only if at left edge
    return atLeft;
  }

  return false;
}

/**
 * Sets up wheel event forwarding from the database iframe to the parent via the bridge.
 * This forwards wheel events to the parent whiteboard only when the user has reached
 * the scroll limits (top or bottom) of the database embed, allowing them to pan the
 * whiteboard when they can no longer scroll within the embed.
 *
 * @returns A cleanup function that removes the event listener
 */
export function setupWheelEventForwarding({
  bridge,
  localClientId,
  externalClientId,
}: ForwardWheelEventsParams): () => void {
  const handleWheel = (event: WheelEvent) => {
    if (shouldForwardWheelEvent(event)) {
      // Prevent default wheel behavior in the database iframe
      event.preventDefault();
      event.stopPropagation();

      // Send wheel event via bridge to parent
      bridge.send(WheelEventCommand, {
        senderId: localClientId,
        receiverId: externalClientId,
        wheelEvent: {
          deltaX: event.deltaX,
          deltaY: event.deltaY,
          clientX: event.clientX,
          clientY: event.clientY,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          viewportWidth: document.documentElement.clientWidth,
          viewportHeight: document.documentElement.clientHeight,
        },
      });
    }
  };

  const eventOptions = {
    passive: false, // Allow preventDefault() to work
    capture: true, // Ensure early interception
  };

  document.addEventListener('wheel', handleWheel, eventOptions);

  // Return cleanup function to remove the event listener
  return () => {
    document.removeEventListener('wheel', handleWheel, eventOptions);
  };
}
